"use client"

import { DialogCard } from "@/components/ui/dialog-card"
import { RealtimeChat } from "@/components/realtime-chat"
import { useEffect, useMemo, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { createClient } from "@/utils/supabase/client"
import { GalleryOpenRefContext } from "@/components/chat/gallery-open-context"
import { ReportDialogOpenContext } from "@/components/chat/report-dialog-open-context"
import { cn } from "@/lib/utils"

interface ChatDialogProps {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  roomName: string
  username: string
  onBack?: () => void
  initialDraft?: { text?: string; imageFile?: File | null; listing?: any; order?: any; sendImmediately?: boolean } | null
  counterpartyBanned?: boolean
}

export const ChatDialog = ({
  open,
  onOpenChangeAction,
  roomName,
  username,
  onBack,
  initialDraft = null,
  counterpartyBanned = false,
}: ChatDialogProps) => {
  const [otherProfile, setOtherProfile] = useState<{ username: string; avatar_url?: string | null } | null>(null)
  const supabase = useMemo(() => createClient(), [])
  const pathname = usePathname()
  const [lastPathname, setLastPathname] = useState<string | null>(null)
  const galleryOpenRef = useRef(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const galleryPortalRef = useRef<HTMLDivElement>(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const reportPortalRef = useRef<HTMLDivElement>(null)
  const reportDialogContextValue = useMemo(
    () => ({ reportDialogOpen, setReportDialogOpen, portalTargetRef: reportPortalRef }),
    [reportDialogOpen]
  )
  const chatContentRef = useRef<HTMLDivElement>(null)
  const savedTabIndicesRef = useRef<Map<HTMLElement, number>>(new Map())

  useEffect(() => {
    const el = chatContentRef.current
    if (!el) return
    if (reportDialogOpen) {
      if (document.activeElement instanceof HTMLElement && el.contains(document.activeElement)) {
        document.activeElement.blur()
      }
      const focusables = el.querySelectorAll<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
      )
      focusables.forEach((node) => {
        const tab = node.getAttribute("tabindex")
        const num = tab === null ? 0 : parseInt(tab, 10)
        if (!Number.isNaN(num) && num >= 0) {
          savedTabIndicesRef.current.set(node, num)
          node.setAttribute("tabindex", "-1")
        }
      })
    } else {
      savedTabIndicesRef.current.forEach((val, node) => {
        if (node.isConnected) node.setAttribute("tabindex", String(val))
      })
      savedTabIndicesRef.current.clear()
    }
  }, [reportDialogOpen])

  // Fetch other user's profile
  useEffect(() => {
    if (!username || !roomName || otherProfile) return

    let mounted = true
    ;(async () => {
      try {
        const { data: profiles, error } = await supabase.rpc('get_public_profiles_with_followers', {
          p_current_user: username
        })
        if (mounted && !error && profiles) {
          const parts = roomName.split(':')
          const otherId = parts.find(p => p !== username)
          if (otherId) {
            const otherProfile = profiles.find((p: any) => p.id === otherId)
            if (otherProfile) {
              setOtherProfile({ username: otherProfile.username, avatar_url: otherProfile.avatar_url })
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error)
      }
    })()
    return () => { mounted = false }
  }, [supabase, username, roomName, otherProfile])

  useEffect(() => {
    if (!open) {
      if (lastPathname !== pathname) setLastPathname(pathname)
      return
    }
    if (lastPathname && lastPathname !== pathname) {
      onOpenChangeAction(false)
    }
    if (lastPathname !== pathname) setLastPathname(pathname)
  }, [lastPathname, onOpenChangeAction, open, pathname])

  const preventCloseWhenGallery = (e: { preventDefault: () => void; detail?: { originalEvent: Event } }) => {
    const ev = e.detail?.originalEvent
    const target = (ev && "target" in ev ? ev.target : null) as HTMLElement | null
    if (!target) return
    if (
      galleryOpenRef.current ||
      target.closest?.("[data-image-gallery]") ||
      target.closest?.("[data-chat-gallery-overlay]") ||
      target.closest?.("[data-report-dialog]")
    ) {
      e.preventDefault()
    }
  }

  const galleryContextValue = useMemo(
    () => ({ galleryOpenRef, isGalleryOpen, setGalleryOpen: setIsGalleryOpen, galleryPortalRef }),
    [isGalleryOpen]
  )

  return (
    <GalleryOpenRefContext.Provider value={galleryContextValue}>
      <ReportDialogOpenContext.Provider value={reportDialogContextValue}>
        <DialogCard
          open={open}
          onOpenChange={onOpenChangeAction}
          /* `<RealtimeChat>` carries the whole surface — its own header with a
             back/close control, composer and scroller — so chrome here would
             be a second one. */
          chrome="none"
          a11yTitle={`Chat with ${otherProfile?.username || "User"}`}
          panelClassName="max-w-2xl h-[80vh]"
          /* The gallery mounts a fullscreen viewer over the dialog; dimming the
             page a second time behind it reads as a black wash. `/30!` is the
             v4 spelling of the important modifier (the class used to be
             `!bg-black/30`, the v3 spelling — still parsed by 4.1's legacy
             branch, but the two forms shouldn't coexist in one repo). The
             modifier is needed because tailwind-merge keeps important and
             non-important classes in separate groups, so `bg-black/70` from
             the overlay token survives the merge and only specificity settles
             it. */
          overlayClassName={cn(isGalleryOpen && "bg-black/30!")}
          onInteractOutside={preventCloseWhenGallery}
          onPointerDownOutside={preventCloseWhenGallery}
          scrollManagedByChild
          contentClassName="px-0 pt-0"
        >
          <div
            ref={galleryPortalRef}
            className="fixed inset-0 z-9000 pointer-events-none"
            aria-hidden={isGalleryOpen ? undefined : true}
          />
          <div
            ref={reportPortalRef}
            className="fixed inset-0 z-99999 pointer-events-none"
            aria-hidden={reportDialogOpen ? undefined : true}
          />
          <div
            ref={chatContentRef}
            className={cn(
              "flex flex-1 flex-col min-h-0 overflow-hidden",
              reportDialogOpen && "pointer-events-none select-none"
            )}
            {...(reportDialogOpen && { inert: true } as React.HTMLAttributes<HTMLDivElement>)}
          >
            <div className="flex-1 min-h-0 overflow-hidden">
              <RealtimeChat
                roomName={roomName}
                username={username}
                onBack={onBack ?? (() => onOpenChangeAction(false))}
                isDialog={true}
                dockToPanel
                initialOtherProfile={otherProfile}
                initialDraft={initialDraft}
                counterpartyBanned={counterpartyBanned}
              />
            </div>
          </div>
        </DialogCard>
      </ReportDialogOpenContext.Provider>
    </GalleryOpenRefContext.Provider>
  )
}
